import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSettings } from "../hooks/useSettings";

export function SortableItem({
  id,
  label,
  active,
  dropdownItems,
  onToggle,
  onDropdownToggle,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const [loadingId, setLoadingId] = useState(null);
  const [dropdownLoading, setDropdownLoading] = useState({});

  const {
    isTogglingNavItem,
    toggleNavItem,
    toggleDropdown,
    isTogglingDropdown,
  } = useSettings();

  const handleToggle = (checked) => {
    if (loadingId === id) return;

    // Call parent's toggle handler immediately for optimistic update
    if (onToggle) {
      onToggle(id, checked);
    }

    // Make API call in background without blocking UI
    setLoadingId(id);
    toggleNavItem(id)
      .then(() => {
        // API call successful - optimistic update was correct
      })
      .catch((error) => {
        console.error("API call failed for item:", id, error);
        // Revert the optimistic update on error
        if (onToggle) {
          onToggle(id, !checked);
        }
      })
      .finally(() => {
        setLoadingId(null);
      });
  };

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
      {...listeners}
      className={`bg-white border px-4 py-2 rounded-md shadow-sm flex flex-col cursor-grab ${
        !active ? "opacity-50" : ""
      }`}
    >
      <div className="flex justify-between items-center">
        <span>{label}</span>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => handleToggle(e.target.checked)}
            disabled={loadingId === id && isTogglingNavItem}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <span className="text-gray-400">≡</span>
        </div>
      </div>
      {dropdownItems && dropdownItems.length > 0 && (
        <div className="ml-4 mt-2">
          <h4 className="text-sm font-medium">Dropdown Items:</h4>
          <ul>
            {dropdownItems.map((dropdown) => (
              <li
                key={dropdown.id}
                className={`bg-gray-50 px-3 py-2 mb-2 border rounded flex items-center justify-between ${
                  !dropdown.active ? "opacity-50" : ""
                }`}
              >
                <span>{dropdown.label}</span>
                <input
                  type="checkbox"
                  checked={dropdown.active}
                  onChange={(e) => {
                    if (dropdownLoading[dropdown.id]) return;
                    const checked = e.target.checked;

                    // Call parent's dropdown toggle handler immediately for optimistic update
                    if (onDropdownToggle) {
                      onDropdownToggle(dropdown.id, checked);
                    }

                    // Make API call in background without blocking UI
                    setDropdownLoading((prev) => ({
                      ...prev,
                      [dropdown.id]: true,
                    }));

                    toggleDropdown(dropdown.id)
                      .then(() => {
                        // API call successful - optimistic update was correct
                      })
                      .catch((error) => {
                        console.error(
                          "Dropdown API call failed for:",
                          dropdown.id,
                          error
                        );
                        // Revert the optimistic update on error
                        if (onDropdownToggle) {
                          onDropdownToggle(dropdown.id, !checked);
                        }
                      })
                      .finally(() => {
                        setDropdownLoading((prev) => ({
                          ...prev,
                          [dropdown.id]: false,
                        }));
                      });
                  }}
                  disabled={dropdownLoading[dropdown.id] && isTogglingDropdown}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

const SortableList = ({
  items,
  onChange,
  renderItem,
  onToggle,
  onDropdownToggle,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!active || !over) {
      console.warn("Invalid drag operation: active or over is undefined.");
      return;
    }

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      console.warn("Invalid indices for drag operation:", {
        oldIndex,
        newIndex,
      });
      return;
    }

    const newArray = arrayMove(items, oldIndex, newIndex);
    onChange(newArray);
  };

  const handleToggle = (itemId, checked) => {
    const updatedItems = items.map((item) =>
      item.id === itemId ? { ...item, active: checked } : item
    );
    onChange(updatedItems);
  };

  const handleDropdownToggle = (dropdownId, checked) => {
    const updatedItems = items.map((item) => ({
      ...item,
      dropdownItems: (item.dropdownItems || []).map((dropdown) =>
        dropdown.id === dropdownId ? { ...dropdown, active: checked } : dropdown
      ),
    }));
    onChange(updatedItems);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <ul className="space-y-2 mt-2">
          {items.map((item) =>
            renderItem ? (
              renderItem(item)
            ) : (
              <SortableItem
                key={item.id}
                id={item.id}
                label={item.label}
                active={item.active}
                dropdownItems={item.dropdownItems}
                onToggle={onToggle || handleToggle}
                onDropdownToggle={onDropdownToggle || handleDropdownToggle}
              />
            )
          )}
        </ul>
      </SortableContext>
    </DndContext>
  );
};

export default SortableList;
